import React from "react";
import { RiLoader5Fill } from "react-icons/ri";
import cx from "classnames";

import {
  DATE_RANGE_OPTIONS,
  BSC_MAINNET,
  CHAIN_NAME_MAPPING,
  MANTLE_MAINNET,
  ARBITRUM,
} from "../constants";

import {
  yaxisFormatterNumber,
  tooltipLabelFormatterUnits,
  tooltipFormatterNumber,
  formatNumber,
  CHART_HEIGHT,
  YAXIS_WIDTH,
  convertToPercents,
} from "../helpers";
import useDateRange from "../hooks/useDateRange";
import {
  useAllChainTotalVolume,
  useAllChainTotalFees,
  useAllChainTotalUsers,
  useAllChainTotalOpenInterest,
  useAllChainTotalTVL,
  useAllChainDailyVolume,
  useAllChainDailyFee,
  useAllChainDailyUsers,
  useAllChainOpenInterest,
  useKtcCirculatingSupply,
  useStakedKtcSupply,
  useKtcHolders,
  useKtcPrice,
} from "../hooks/useAllChainsData";

import DateRangeSelect from "../components/DateRangeSelect";
import { AllChainVolumeChart } from "../components/VolumeChart";
import { AllFeesChart } from "../components/FeesChart";
import GenericChart from "../components/GenericChart";

const NOW = Math.floor(Date.now() / 1000);

function All() {
  const { dataRange, onDateRangeChange } = useDateRange();
  const [totalVolume, totalVolumeDelta] = useAllChainTotalVolume();
  const [totalFees, totalFeesDelta] = useAllChainTotalFees();
  const [totalUsers, totalUsersDelta] = useAllChainTotalUsers();
  const [openInterest, openInterestDelta] = useAllChainTotalOpenInterest();
  const [totalAum, totalAumDelta] = useAllChainTotalTVL();
  const circulatingSupply = useKtcCirculatingSupply();
  const stakedKtcSupply = useStakedKtcSupply();
  const ktcHolders = useKtcHolders();
  const ktcPrice = useKtcPrice();
  const stakedKtcUsd =
    ktcPrice && stakedKtcSupply
      ? parseFloat(ktcPrice) * parseFloat(stakedKtcSupply)
      : undefined;

  const from = dataRange.fromValue
    ? Math.floor(+new Date(dataRange.fromValue) / 1000)
    : undefined;

  const to = dataRange.toValue
    ? Math.floor(+new Date(dataRange.toValue) / 1000)
    : NOW;

  const [dailyVolume, isVolumeLoading] = useAllChainDailyVolume(from, to);
  const [feesData, feesLoading] = useAllChainDailyFee(from, to);
  const [usersData, usersLoading] = useAllChainDailyUsers(from, to);
  const [openInterests, openInterestLoading] = useAllChainOpenInterest(
    from,
    to
  );

  return (
    <div className="Home">
      <div className="chart-grid all">
        <div className="chart-cell stats ">
          {totalVolume ? (
            <>
              <div className="total-stat-label">Total Volume</div>
              <div className="total-stat-value">
                {formatNumber(totalVolume, { currency: true })}
                {totalVolumeDelta && (
                  <span
                    className="total-stat-delta plus"
                    title="Change since previous day"
                  >
                    +
                    {formatNumber(totalVolumeDelta, {
                      currency: true,
                      compact: true,
                    })}
                  </span>
                )}
              </div>
            </>
          ) : (
            <RiLoader5Fill size="3em" className="loader" />
          )}
        </div>
        <div className="chart-cell stats">
          {totalFees ? (
            <>
              <div className="total-stat-label">Total Fees</div>
              <div className="total-stat-value">
                {formatNumber(totalFees, { currency: true })}
                <span
                  className="total-stat-delta plus"
                  title="Change since previous day"
                >
                  +
                  {formatNumber(totalFeesDelta, {
                    currency: true,
                    compact: true,
                  })}
                </span>
              </div>
            </>
          ) : (
            <RiLoader5Fill size="3em" className="loader" />
          )}
        </div>
        <div className="chart-cell stats">
          {totalAum && stakedKtcUsd ? (
            <>
              <div className="total-stat-label">Total Value Locked</div>
              <div className="total-stat-value">
                {formatNumber(totalAum + stakedKtcUsd, { currency: true })}
                <span
                  className={cx(
                    "total-stat-delta",
                    totalAumDelta > 0 ? "plus" : "minus"
                  )}
                  title="Change since previous day"
                >
                  {totalAumDelta > 0 ? "+" : ""}
                  {formatNumber(totalAumDelta, {
                    currency: true,
                    compact: true,
                  })}
                </span>
              </div>
            </>
          ) : (
            <RiLoader5Fill size="3em" className="loader" />
          )}
        </div>
        <div className="chart-cell stats">
          {totalUsers ? (
            <>
              <div className="total-stat-label">Total Users</div>
              <div className="total-stat-value">
                {formatNumber(totalUsers)}
                <span
                  className="total-stat-delta plus"
                  title="Change since previous day"
                >
                  +
                  {formatNumber(totalUsersDelta, {
                    compact: true,
                    compactDigit: 0,
                  })}
                </span>
              </div>
            </>
          ) : (
            <RiLoader5Fill size="3em" className="loader" />
          )}
        </div>
        <div className="chart-cell stats">
          {openInterest !== undefined ? (
            <>
              <div className="total-stat-label">Open Interest</div>
              <div className="total-stat-value">
                {formatNumber(openInterest, { currency: true })}
                <span
                  className={cx(
                    "total-stat-delta",
                    openInterestDelta > 0 ? "plus" : "minus"
                  )}
                  title="Change since previous day"
                >
                  {openInterestDelta > 0 ? "+" : ""}
                  {formatNumber(openInterestDelta, {
                    currency: true,
                    compact: true,
                  })}
                </span>
              </div>
            </>
          ) : (
            <RiLoader5Fill size="3em" className="loader" />
          )}
        </div>
        <div className="chart-cell stats">
          {circulatingSupply ? (
            <>
              <div className="total-stat-label">KTC Circulating Supply</div>
              <div className="total-stat-value">
                {formatNumber(circulatingSupply, { currency: false })}
              </div>
            </>
          ) : (
            <RiLoader5Fill size="3em" className="loader" />
          )}
        </div>
        <div className="chart-cell stats">
          {stakedKtcSupply ? (
            <>
              <div className="total-stat-label">Staked KTC Supply</div>
              <div className="total-stat-value">
                {formatNumber(stakedKtcSupply, { currency: false })}
              </div>
            </>
          ) : (
            <RiLoader5Fill size="3em" className="loader" />
          )}
        </div>
        <div className="chart-cell stats">
          {ktcHolders ? (
            <>
              <div className="total-stat-label">Number of KTC Holders</div>
              <div className="total-stat-value">
                {formatNumber(ktcHolders, { currency: false })}
              </div>
            </>
          ) : (
            <RiLoader5Fill size="3em" className="loader" />
          )}
        </div>
      </div>

      <div className="form">
        <DateRangeSelect
          options={DATE_RANGE_OPTIONS}
          startDate={dataRange.fromValue}
          endDate={dataRange.toValue}
          onChange={onDateRangeChange}
        />
      </div>

      <div className="chart-grid">
        <div className="chart-cell">
          <AllChainVolumeChart
            data={dailyVolume}
            loading={isVolumeLoading}
            chartHeight={CHART_HEIGHT}
            yaxisWidth={YAXIS_WIDTH}
          />
        </div>
        <div className="chart-cell">
          <AllFeesChart
            data={feesData}
            loading={feesLoading}
            chartHeight={CHART_HEIGHT}
            yaxisWidth={YAXIS_WIDTH}
          />
        </div>
        <div className="chart-cell">
          <GenericChart
            loading={usersLoading}
            title="DAU"
            data={usersData}
            truncateYThreshold={6500}
            yaxisDataKey="all"
            rightYaxisDataKey="cumulative"
            yaxisTickFormatter={yaxisFormatterNumber}
            tooltipFormatter={tooltipFormatterNumber}
            tooltipLabelFormatter={tooltipLabelFormatterUnits}
            items={[
              { key: MANTLE_MAINNET, name: CHAIN_NAME_MAPPING[MANTLE_MAINNET] },
              { key: BSC_MAINNET, name: CHAIN_NAME_MAPPING[BSC_MAINNET] },
              { key: ARBITRUM, name: CHAIN_NAME_MAPPING[ARBITRUM] },
            ]}
          />
        </div>
        <div className="chart-cell">
          <GenericChart
            loading={openInterestLoading}
            title="Open Interest"
            data={openInterests}
            controls={{
              convertToPercents: convertToPercents,
            }}
            yaxisDataKey="all"
            items={[
              { key: MANTLE_MAINNET, name: CHAIN_NAME_MAPPING[MANTLE_MAINNET] },
              { key: BSC_MAINNET, name: CHAIN_NAME_MAPPING[BSC_MAINNET] },
              { key: ARBITRUM, name: CHAIN_NAME_MAPPING[ARBITRUM] },
            ]}
            description="Open Interest is the sum of Long Open Interest and Short Open Interest"
          />
        </div>
      </div>
    </div>
  );
}

export default All;
